import type {
  Exercise,
  ExerciseDefinition,
} from "@evidenceb/athena-common/interfaces/Exercise";
import type { Gameplay } from "@evidenceb/athena-common/interfaces/Gameplay";
import { getGameplay } from "@evidenceb/gameplays/registry";
import Case from "case";

export class UnavailableGameplayError extends Error {}

/**
 * Function that returns a list of exercises with their associated gameplay. If
 * the gameplay required for a provided exercise cannot be found, it if not
 * added to the returned list.
 * NB: If more than logging is required from the callback, have a look at https://github.com/EvidenceB/evb_application/pull/6#pullrequestreview-573054253
 * @param exerciseDefinitions The list of raw exercises
 * @param onExerciseError A callback that is called for every exercise for which the Gameplay cannot be imported (should be used for logging)
 */
export const getExercisesWithAvailableGameplays = async <
  T extends { type: ExerciseDefinition["type"] },
>(
  exerciseDefinitions: T[],
): Promise<(T & { Gameplay: Exercise["Gameplay"] })[]> => {
  const settledPromises = await fetchGameplays(exerciseDefinitions);
  return settledPromises.map((settledPromise) => {
    if (
      settledPromise.status === "fulfilled" &&
      typeof settledPromise.value !== "undefined"
    )
      return settledPromise.value;
    else {
      throw new Error(); // TODO: error details
    }
  });
};

/**
 * An asynchronous function that returns a list of promise results from
 * fetching a component for each provided raw exercise
 * @param exerciseDefinitions The list of raw exercise
 */
const fetchGameplays = async <T extends { type: ExerciseDefinition["type"] }>(
  exerciseDefinitions: T[],
): Promise<
  (
    | PromiseFulfilledResult<T & { Gameplay: Exercise["Gameplay"] }>
    | PromiseRejectedResult
  )[]
> => {
  // Import each gameplay once: WebKit does not dedupe concurrent dynamic
  // imports of the same module, and there are thousands of exercises
  const gameplays = new Map<string, Promise<Gameplay<any, any>>>();
  const getComponent = (type: string) => {
    if (!gameplays.has(type)) gameplays.set(type, fetchComponent(type));
    return gameplays.get(type)!;
  };
  return Promise.allSettled(
    exerciseDefinitions.map(async (exerciseDefinition) => ({
      ...exerciseDefinition,
      Gameplay: await getComponent(exerciseDefinition.type),
    })),
  );
};

/**
 * An asynchronous function that fetches the gameplay associated with a type
 * @param gameplayType
 */
export const fetchComponent = async (
  gameplayType: string,
): Promise<Gameplay<any, any>> => {
  if (!gameplayType) {
    const message = "Gameplay type is missing.";
    console.error(message);
    throw new Error(message);
  }
  const gameplayPascal = Case.pascal(gameplayType);
  try {
    return await getGameplay(gameplayType);
  } catch (err) {
    const message = `Gameplay ${gameplayPascal} not available.`;
    console.error(message, err);
    throw new Error(message);
  }
};

export default fetchGameplays;
